// Global Instructions Rule Applied!
//
// Phase 5 — Sent Log tab (audit-log grid).
//
// Same view__email_campaigns data RPC as the Drafts tab, but filtered to
// status ≠ 'draft' and rendered with the audit columns the Phase 5 plan
// calls for: both Created by (operator) and Send as (From identity) so
// send-on-behalf events are auditable, plus Sent / Failed / Dispatched /
// Completed timestamps.
//
// Filter UX uses a row of AntD Select dropdowns wired to the new
// view__email_campaigns_filter_options RPC (one fetch on mount). The
// status / template / send-as / created-by lists are multiselect; the
// dispatched-at uses an AntD RangePicker that emits an
// {min, max} ISO-timestamp object — the data RPC's filter handler parses
// that shape verbatim.
//
// Read-only: clicking a row opens the existing compose form in read-only
// mode (the Full.tsx already handles non-draft campaigns as read-only).

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import type { EmailCampaignGridModel } from '../../../state_manager/data_models/email_campaign_grid_model.ts'
import { useEmailCampaigns } from '../hooks/useEmailCampaigns.ts'
import {
  fetchEmailCampaignsFilterOptions,
  type EmailCampaignsFilterOptions,
  type FilterOption
} from '../services/emailService.ts'
import type { FilterValue } from '../../../state_manager/interfaces/email.ts'

const { Text } = Typography
const EMAIL_MODULE = 'email'

// Same status palette as the original page; the Sent log only sees the
// non-draft tail (queued onward) so 'draft' is excluded but kept in the
// map for the unlikely event of cross-tab navigation. Labels duplicated
// from view__email_campaigns_filter_options so the table's Status tag
// stays in lockstep with the dropdown's display text — both render
// human-friendly strings, both filter on the raw enum value.
const STATUS_COLOR: Record<string, string> = {
  draft: 'default',
  queued: 'blue',
  sending: 'processing',
  // Operator-held (Halt); needs attention.
  halted: 'gold',
  sent: 'green',
  partially_failed: 'orange',
  failed: 'red',
  cancelled: 'default'
}

const STATUS_LABEL: Record<string, string> = {
  draft:            'Draft',
  queued:           'Queued',
  sending:          'Sending',
  halted:           'Halted',
  sent:             'Sent',
  partially_failed: 'Partially failed',
  failed:           'Failed',
  cancelled:        'Cancelled'
}

const NON_DRAFT_STATUSES = [
  'queued',
  'sending',
  'halted',
  'sent',
  'partially_failed',
  'failed',
  'cancelled'
]

// Initial filter: every status except draft. Pre-pinned so the tab loads
// only "ever-sent" campaigns; users can narrow further via the multiselect.
const INITIAL_FILTERS: FilterValue[] = [
  { key: 'email_campaigns__status', value: NON_DRAFT_STATUSES }
]

// Default sort: dispatched_at DESC (freshest send at the top). Falls back
// to date_modified DESC when dispatched_at is NULL (the data RPC sorts
// NULLS LAST).
const INITIAL_SORTING = [
  { key: 'email_campaigns__dispatched_at', direction: 'desc' as const }
]

const formatDate = (iso?: string | null) => {
  if (!iso) return <span style={{ color: '#9ca3af' }}>—</span>
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const EMPTY_FILTER_OPTIONS: EmailCampaignsFilterOptions = {
  email_campaigns__status: [],
  email_campaigns__email_template_id: [],
  email_campaigns__sent_as_user_id: [],
  email_campaigns__created_by: []
}

interface SelectedFilters {
  statuses: string[]
  templateIds: string[]
  sentAsUserIds: string[]
  createdByIds: string[]
  dispatchedRange: [Dayjs | null, Dayjs | null] | null
}

const EMPTY_SELECTED: SelectedFilters = {
  // Status starts EMPTY so the dropdown reads cleanly on first paint;
  // buildFiltersFromSelections falls back to NON_DRAFT_STATUSES when the
  // selection is empty, so the query still hides drafts (the Drafts tab
  // covers them). The user can narrow by selecting specific statuses;
  // clearing reverts to "all non-draft" without re-populating tags.
  statuses: [],
  templateIds: [],
  sentAsUserIds: [],
  createdByIds: [],
  dispatchedRange: null
}

function buildFiltersFromSelections(sel: SelectedFilters): FilterValue[] {
  const out: FilterValue[] = []
  // Status is special: NEVER let it go empty (would surface drafts too).
  const statuses = sel.statuses.length > 0 ? sel.statuses : NON_DRAFT_STATUSES
  out.push({ key: 'email_campaigns__status', value: statuses })
  if (sel.templateIds.length > 0) {
    out.push({ key: 'email_campaigns__email_template_id', value: sel.templateIds })
  }
  if (sel.sentAsUserIds.length > 0) {
    out.push({ key: 'email_campaigns__sent_as_user_id', value: sel.sentAsUserIds })
  }
  if (sel.createdByIds.length > 0) {
    out.push({ key: 'email_campaigns__created_by', value: sel.createdByIds })
  }
  if (sel.dispatchedRange) {
    const [min, max] = sel.dispatchedRange
    if (min || max) {
      out.push({
        key: 'email_campaigns__dispatched_at',
        value: {
          min: min ? min.toISOString() : '',
          max: max ? max.toISOString() : ''
        }
      })
    }
  }
  return out
}

export default function SentLogTab() {
  const {
    campaignsData,
    campaignsLoading,
    activeSorting,
    handleSetFilters,
    handleSetSorting,
    refetch
  } = useEmailCampaigns({
    initialFilters: INITIAL_FILTERS,
    initialSorting: INITIAL_SORTING
  })
  const { openFormInTab } = useTabManagerContext()
  const currentSort = activeSorting?.[0]

  const [options, setOptions] = useState<EmailCampaignsFilterOptions>(EMPTY_FILTER_OPTIONS)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [selected, setSelected] = useState<SelectedFilters>(EMPTY_SELECTED)

  useEffect(() => {
    let cancelled = false
    setOptionsLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const resp = await fetchEmailCampaignsFilterOptions(token)
      if (cancelled) return
      setOptions(resp.data)
      setOptionsLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Push selected filters → hook on every change.
  useEffect(() => {
    handleSetFilters(buildFiltersFromSelections(selected))
  }, [selected, handleSetFilters])

  const openDetail = useCallback(
    (record: EmailCampaignGridModel) => {
      openFormInTab(EMAIL_MODULE, record.email_campaigns__id, record.email_campaigns__name)
    },
    [openFormInTab]
  )

  // Status options exclude 'draft' (the Drafts tab covers that).
  const statusOptions = useMemo<FilterOption[]>(
    () => options.email_campaigns__status.filter((o) => o.value !== 'draft'),
    [options]
  )

  const columns: ColumnsType<EmailCampaignGridModel> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'email_campaigns__name',
        key: 'email_campaigns__name',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__name'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (name: string, record: EmailCampaignGridModel) => (
          <a onClick={() => openDetail(record)} className='font-medium'>
            {name || '(untitled)'}
          </a>
        )
      },
      {
        title: 'Subject',
        dataIndex: 'email_campaigns__subject',
        key: 'email_campaigns__subject',
        render: (subject: string) =>
          subject ? subject : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Template',
        dataIndex: 'email_campaigns__email_template_name',
        key: 'email_campaigns__email_template_name',
        width: 160,
        render: (label: string) =>
          label ? <Tag>{label}</Tag> : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Created by',
        dataIndex: 'email_campaigns__created_by_name',
        key: 'email_campaigns__created_by_name',
        width: 160,
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__created_by_name'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (name: string | null | undefined) =>
          name ? name : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Send as',
        dataIndex: 'email_campaigns__sent_as_user_name',
        key: 'email_campaigns__sent_as_user_name',
        width: 160,
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__sent_as_user_name'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (name: string) =>
          name ? name : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Status',
        dataIndex: 'email_campaigns__status',
        key: 'email_campaigns__status',
        width: 130,
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__status'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (status: string) => (
          <Tag color={STATUS_COLOR[status] || 'default'}>{STATUS_LABEL[status] || status}</Tag>
        )
      },
      {
        title: 'Recipients',
        dataIndex: 'email_campaigns__recipient_count',
        key: 'email_campaigns__recipient_count',
        width: 110,
        align: 'right',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__recipient_count'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (n: number) => (n > 0 ? n : <span style={{ color: '#9ca3af' }}>—</span>)
      },
      {
        title: 'Sent',
        dataIndex: 'email_campaigns__sent_count',
        key: 'email_campaigns__sent_count',
        width: 90,
        align: 'right',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__sent_count'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (n: number) =>
          typeof n === 'number' && n >= 0 ? n : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Failed',
        dataIndex: 'email_campaigns__failed_count',
        key: 'email_campaigns__failed_count',
        width: 90,
        align: 'right',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__failed_count'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (n: number) =>
          typeof n === 'number' && n > 0
            ? <Text type='danger'>{n}</Text>
            : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Dispatched',
        dataIndex: 'email_campaigns__dispatched_at',
        key: 'email_campaigns__dispatched_at',
        width: 180,
        sorter: true,
        defaultSortOrder: 'descend',
        sortOrder:
          currentSort?.key === 'email_campaigns__dispatched_at'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (iso: string | null | undefined) => formatDate(iso)
      },
      {
        title: 'Completed',
        dataIndex: 'email_campaigns__completed_at',
        key: 'email_campaigns__completed_at',
        width: 180,
        render: (iso: string | null | undefined) => formatDate(iso)
      }
    ],
    [currentSort, openDetail]
  )

  const handleTableChange = useCallback(
    (_pagination: unknown, _filters: unknown, sorter: any) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter
      if (s && s.order && s.columnKey) {
        handleSetSorting([
          { key: String(s.columnKey), direction: s.order === 'descend' ? 'desc' : 'asc' }
        ])
      } else {
        handleSetSorting(INITIAL_SORTING)
      }
    },
    [handleSetSorting]
  )

  const onRefresh = useCallback(() => {
    refetch()
  }, [refetch])

  return (
    <div className='flex flex-col h-full'>
      {/* Filter bar */}
      <div className='flex items-center gap-2 mb-3 flex-wrap'>
        <Select
          mode='multiple'
          allowClear
          placeholder='Status'
          loading={optionsLoading}
          value={selected.statuses}
          // AntD's allowClear X button on a multiselect emits undefined
          // (not []). Coerce here so the rest of the pipeline can assume
          // an array — buildFiltersFromSelections falls back to
          // NON_DRAFT_STATUSES when statuses is empty so the table stays
          // populated when the user clears.
          onChange={(v) => setSelected((s) => ({ ...s, statuses: Array.isArray(v) ? v : [] }))}
          style={{ minWidth: 200 }}
          options={statusOptions}
          maxTagCount='responsive'
        />
        <Select
          mode='multiple'
          allowClear
          placeholder='Template'
          loading={optionsLoading}
          value={selected.templateIds}
          onChange={(v) => setSelected((s) => ({ ...s, templateIds: Array.isArray(v) ? v : [] }))}
          style={{ minWidth: 200 }}
          options={options.email_campaigns__email_template_id}
          maxTagCount='responsive'
        />
        <Select
          mode='multiple'
          allowClear
          placeholder='Created by'
          loading={optionsLoading}
          value={selected.createdByIds}
          onChange={(v) => setSelected((s) => ({ ...s, createdByIds: Array.isArray(v) ? v : [] }))}
          style={{ minWidth: 220 }}
          options={options.email_campaigns__created_by}
          maxTagCount='responsive'
        />
        <Select
          mode='multiple'
          allowClear
          placeholder='Send as'
          loading={optionsLoading}
          value={selected.sentAsUserIds}
          onChange={(v) => setSelected((s) => ({ ...s, sentAsUserIds: Array.isArray(v) ? v : [] }))}
          style={{ minWidth: 220 }}
          options={options.email_campaigns__sent_as_user_id}
          maxTagCount='responsive'
        />
        <DatePicker.RangePicker
          placeholder={['Dispatched from', 'to']}
          allowEmpty={[true, true]}
          value={selected.dispatchedRange ?? undefined}
          onChange={(range) => {
            setSelected((s) => ({
              ...s,
              dispatchedRange: range
                ? [range[0] ?? null, range[1] ?? null]
                : null
            }))
          }}
          showTime={false}
        />
        <Space className='ml-auto'>
          <Tooltip title='Refresh'>
            <Button icon={<ReloadOutlined />} onClick={onRefresh} />
          </Tooltip>
        </Space>
      </div>

      <div className='flex-1 min-h-0 overflow-auto'>
        <Table<EmailCampaignGridModel>
          className='email-campaigns-table'
          rowKey='email_campaigns__id'
          columns={columns}
          dataSource={campaignsData}
          loading={campaignsLoading}
          onChange={handleTableChange}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          size='middle'
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  )
}
